"use client";

import { useEffect, useState } from "react";
import { CalendarDays, Clock, ChevronRight, BookOpen, Search, Filter } from "lucide-react";
import { Header } from "../components/Header";
import Footer from "../components/Footer";
import { blogContent } from "../data/blog";
import { Helmet } from "react-helmet";

// Fetch blog posts
const fetchBlogs = async () => {
  return blogContent;
};

// Categories derived from blog tags
const getCategories = (blogs) => {
  const categoriesSet = new Set();
  blogs.forEach(blog => {
    if (blog.tags && Array.isArray(blog.tags)) {
      blog.tags.forEach(tag => categoriesSet.add(tag));
    }
  });
  return Array.from(categoriesSet);
};

export default function BlogsPage() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Track page view for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Blog & Insights',
        page_path: '/blog',
        page_location: window.location.href
      });
    }

    const fetchPosts = async () => {
      try {
        const blogsData = await fetchBlogs();
        
        if (blogsData.length > 0) {
          // Set the first blog as featured and the rest as regular blogs
          setFeaturedBlog(blogsData[0]);
          setBlogs(blogsData.slice(1));
          setFilteredBlogs(blogsData.slice(1));
          
          // Extract categories from blogs
          setCategories(getCategories(blogsData));
        } else {
          setBlogs([]);
          setFilteredBlogs([]);
        }
        
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    fetchPosts();
  }, []);

  // Filter blogs based on search term and category
  useEffect(() => {
    if (blogs.length > 0) {
      let results = [...blogs];
      
      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        results = results.filter(blog => 
          blog.title.toLowerCase().includes(term) || 
          blog.description.toLowerCase().includes(term) ||
          (blog.tags && blog.tags.some(tag => tag.toLowerCase().includes(term)))
        );
      }
      
      // Filter by category
      if (selectedCategory) {
        results = results.filter(blog => 
          blog.tags && blog.tags.includes(selectedCategory)
        );
      }
      
      setFilteredBlogs(results);
    }
  }, [searchTerm, selectedCategory, blogs]);

  const pageTitle = searchTerm || selectedCategory 
    ? `${searchTerm ? `"${searchTerm}" ` : ''}${selectedCategory ? `${searchTerm ? 'in ' : ''}${selectedCategory} ` : ''}| Blog & Insights | HotelHero.fun`
    : "Blog & Insights | HotelHero.fun";
    
  const metaDescription = "Explore the latest insights, tips, and strategies for hotel management simulation in HotelHero.fun. Our blog covers game mechanics, optimization strategies, and advanced techniques.";
  const metaKeywords = categories.length > 0 
    ? `HotelHero.fun, hotel management simulation, ${categories.join(', ')}, hotel tycoon tips`
    : "HotelHero.fun, hotel management simulation, game strategy, hotel tycoon tips, hotel management game";
  

  return (
    <>
     <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        
        {/* Canonical URL - prevents duplicate content issues */}
        <link rel="canonical" href={`https://hotelhero.fun/blog${searchTerm || selectedCategory ? '?' : ''}${searchTerm ? `search=${encodeURIComponent(searchTerm)}` : ''}${searchTerm && selectedCategory ? '&' : ''}${selectedCategory ? `category=${encodeURIComponent(selectedCategory)}` : ''}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://hotelhero.fun/blog'} />
        <meta property="og:site_name" content="HotelHero.fun" />
        <meta property="og:image" content="https://hotelhero.fun/logo.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://hotelhero.fun/logo.png" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="HotelHero.fun Team" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#7200B8" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        
        {/* Schema.org structured data - dynamically generated based on content */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "headline": pageTitle,
            "description": metaDescription,
            "image": "https://hotelhero.fun/logo.png",
            "url": typeof window !== 'undefined' ? window.location.href : 'https://hotelhero.fun/blog',
            "dateModified": new Date().toISOString(),
            "author": {
              "@type": "Organization",
              "name": "HotelHero.fun",
              "logo": {
                "@type": "ImageObject",
                "url": "https://hotelhero.fun/logo.png"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "HotelHero.fun",
              "logo": {
                "@type": "ImageObject",
                "url": "https://hotelhero.fun/logo.png"
              }
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": featuredBlog && [featuredBlog, ...filteredBlogs].map((blog, index) => ({
                "@type": "BlogPosting",
                "position": index + 1,
                "url": `https://hotelhero.fun/blog/${blog.slug}`,
                "headline": blog.title,
                "description": blog.description,
                "image": blog.coverImage || blog.image || "https://hotelhero.fun/images/default-blog-image.jpg",
                "datePublished": blog.publishDate,
                "dateModified": blog.modifiedDate || blog.publishDate,
                "author": {
                  "@type": "Person",
                  "name": blog.author?.name || "HotelHero.fun Team"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "HotelHero.fun",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://hotelhero.fun/logo.png"
                  }
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://hotelhero.fun/blog/${blog.slug}`
                }
              }))
            }
          })}
        </script>
      </Helmet>
      
      <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-[100dvh] text-white">
        {/* Hero Section */}
        <div className="container mx-auto px-4 pt-8 pb-20">
          <Header />
          <main className="grow mt-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              {/* Header section */}
              <div className="text-center mb-12">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Blog & Insights
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Discover the latest insights, strategies, and tips for mastering HotelHero.fun and dominating the hotel management simulation world.
                </p>
              </div>

              {/* Search and filter section */}
              <div className="mb-12 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 
                    border border-gray-300 
                    rounded-md leading-5
                     bg-white placeholder-gray-500 
                     focus:outline-none 
                     text-black
                     focus:ring-2 focus:ring-[#7200B8] 
                     focus:border-[#7200B8] sm:text-sm"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search articles"
                  />
                </div>
                
                {categories.length > 0 && (
                  <div className="relative inline-block text-left">
                    <div className="flex items-center">
                      <Filter size={18} className="text-gray-400 mr-2" />
                      <select
                        className="block w-full py-2 pl-3 pr-10 text-base border-gray-300 focus:outline-none focus:ring-[#7200B8] focus:border-[#7200B8] sm:text-sm rounded-md"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        aria-label="Filter by category"
                      >
                        <option value="">All Categories</option>
                        {categories.map((category, index) => (
                          <option key={index} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Featured post */}
              {featuredBlog && !searchTerm && !selectedCategory && (
                <div className="mb-16">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Post</h2>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden transition-transform hover:scale-[1.01] hover:shadow-lg">
                    <a href={`/blog/${featuredBlog.slug}`} className="block" aria-label={`Read ${featuredBlog.title}`}>
                      <div className="md:flex">
                        <div className="md:w-1/3 relative h-64 md:h-auto">
                          <div className="h-full w-full relative">
                            <div 
                              className="absolute inset-0 bg-center bg-cover"
                              style={{ 
                                backgroundImage: `url(${featuredBlog.coverImage || featuredBlog.image || 'https://via.placeholder.com/600x400?text=HotelHero.fun+Blog'})`,
                              }}
                              aria-hidden="true"
                            >
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-br from-[#7200B8]/30 to-transparent" aria-hidden="true"></div>
                          </div>
                        </div>
                        <div className="md:w-2/3 p-6 md:p-8">
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="bg-purple-100 text-[#7200B8] text-xs font-semibold px-3 py-1 rounded-full">
                              Featured
                            </span>
                            {featuredBlog.tags && featuredBlog.tags[0] && (
                              <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full">
                                {featuredBlog.tags[0]}
                              </span>
                            )}
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                            {featuredBlog.title}
                          </h3>
                          <p className="text-gray-600 mb-6 line-clamp-3">
                            {featuredBlog.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-[#7200B8] mr-3">
                                {featuredBlog.author?.avatar ? (
                                  <img 
                                    src={featuredBlog.author.avatar} 
                                    alt={`${featuredBlog.author.name}'s avatar`}
                                    className="w-10 h-10 rounded-full object-cover"
                                    width="40"
                                    height="40"
                                    loading="eager"
                                  />
                                ) : (
                                  <span aria-hidden="true">
                                    {featuredBlog.author?.name?.charAt(0) || "H"}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {featuredBlog.author?.name || "HotelHero.fun Team"}
                                </p>
                                <div className="flex items-center text-xs text-gray-500">
                                  <CalendarDays size={14} className="mr-1" aria-hidden="true" />
                                  <time dateTime={new Date(featuredBlog.publishDate).toISOString()}>
                                    {featuredBlog.publishDate}
                                  </time>
                                  <span className="mx-2">•</span>
                                  <Clock size={14} className="mr-1" aria-hidden="true" />
                                  <span>{featuredBlog.readingTime || "5 min read"}</span>
                                </div>
                              </div>
                            </div>
                            <div className="hidden md:block">
                              <span className="inline-flex items-center text-[#7200B8] font-medium">
                                Read more <ChevronRight size={16} className="ml-1" aria-hidden="true" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
              )}

              {/* Blog posts grid */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {searchTerm || selectedCategory ? 'Search Results' : 'Latest Posts'}
                </h2>
                {filteredBlogs && filteredBlogs.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBlogs.map((blog) => (
                      <a 
                        href={`/blog/${blog.slug}`}
                        key={blog.slug}
                        className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-transform hover:scale-[1.02]"
                        aria-label={`Read ${blog.title}`}
                      >
                        <div className="h-48 overflow-hidden relative">
                          <div 
                            className="absolute inset-0 bg-center bg-cover transform group-hover:scale-105 transition-transform duration-300"
                            style={{ 
                              backgroundImage: `url(${blog.image || blog.image || 'https://via.placeholder.com/600x400?text=HotelHero.fun+Blog'})`,
                            }}
                            aria-hidden="true"
                          >
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" aria-hidden="true"></div>
                        </div>
                        <div className="p-6">
                          {blog.tags && blog.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {blog.tags.slice(0, 2).map((tag, index) => (
                                <span 
                                  key={index} 
                                  className="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">{blog.title}</h3>
                          <p className="text-gray-600 mb-4 line-clamp-3">{blog.description}</p>
                          <div className="flex items-center justify-between mt-auto">
                            <div className="flex items-center text-xs text-gray-500">
                              <CalendarDays size={14} className="mr-1" aria-hidden="true" />
                              <time dateTime={new Date(blog.publishDate).toISOString()}>
                                {blog.publishDate}
                              </time>
                              <span className="mx-2">•</span>
                              <Clock size={14} className="mr-1" aria-hidden="true" />
                              <span>{blog.readingTime || "5 min read"}</span>
                            </div>
                            <span className="text-[#7200B8] text-sm font-medium flex items-center">
                              Read <ChevronRight size={16} className="ml-1" aria-hidden="true" />
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <BookOpen size={48} className="mx-auto text-gray-300 mb-4" aria-hidden="true" />
                    {searchTerm || selectedCategory ? (
                      <>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No matching blog posts found</h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                          Try adjusting your search terms or category filter to find what you're looking for.
                        </p>
                        <button 
                          onClick={() => { setSearchTerm(""); setSelectedCategory(""); }}
                          className="mt-4 bg-[#7200B8] hover:bg-[#5a0094] text-white font-medium py-2 px-4 rounded-md transition-colors"
                        >
                          Clear filters
                        </button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Blog Posts Yet</h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                          We're working on creating valuable content for you. Check back soon for insightful articles on hotel management and more.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Newsletter subscription - commented out in the original, keeping it that way */}
              {/* 
              <div className="mt-16 bg-gradient-to-r from-[#7200B8] to-[#9b4dff] rounded-xl shadow-lg overflow-hidden">
                <div className="md:flex items-center">
                  <div className="md:w-2/3 p-8 md:p-12">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                      Stay updated with our latest insights
                    </h3>
                    <p className="text-purple-100 mb-6">
                      Subscribe to our newsletter for the latest articles, guides, and industry trends.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-white flex-grow"
                        required
                      />
                      <button
                        type="submit"
                        className="bg-white text-[#7200B8] font-medium px-6 py-3 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        Subscribe
                      </button>
                    </form>
                  </div>
                  <div className="hidden md:block md:w-1/3 relative h-full">
                    <div className="h-full min-h-[12rem] flex items-center justify-center">
                      <div className="transform -rotate-6 bg-white/10 p-6 rounded-lg backdrop-blur-sm">
                        <div className="transform rotate-6">
                          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                            <BookOpen size={32} className="text-[#7200B8]" />
                          </div>
                          <div className="space-y-2">
                            <div className="h-2 bg-white/70 rounded-full w-32"></div>
                            <div className="h-2 bg-white/50 rounded-full w-24"></div>
                            <div className="h-2 bg-white/30 rounded-full w-28"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              */}
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </>
  );
}